// loaders.gl
// SPDX-License-Identifier: MIT
// Copyright (c) vis.gl contributors
/** Checks whether a value is a boolean */
const isBoolean = (value) => typeof value === 'boolean';
/** Checks whether a value is a function */
const isFunction = (value) => typeof value === 'function';
/** Checks whether a value is a non-null object */
export const isObject = (value) => value !== null && typeof value === 'object';
/** Checks whether a value is a plain object (created by the Object constructor) */
export const isPureObject = (value) => isObject(value) && value.constructor === {}.constructor;
/** Checks whether a value is an ArrayBuffer */
export const isArrayBuffer = (value) => typeof ArrayBuffer !== 'undefined' && value instanceof ArrayBuffer;
/** Checks whether a value is an ArrayBuffer */
export const isSharedArrayBuffer = (value) => typeof SharedArrayBuffer !== 'undefined' && value instanceof SharedArrayBuffer;
/** Checks whether a value is ArrayBuffer-like */
export const isArrayBufferLike = (value) => isObject(value) &&
    typeof value.byteLength === 'number' &&
    typeof value.slice === 'function';
/** Checks whether a value behaves like a promise */
export const isPromise = (value) => isObject(value) && 'then' in value && isFunction(value.then);
/** Checks whether a value implements the iterable protocol */
export const isIterable = (value) => Boolean(value) && isFunction(value[Symbol.iterator]);
/** Checks whether a value implements the async iterable protocol */
export const isAsyncIterable = (value) => Boolean(value) && isFunction(value[Symbol.asyncIterator]);
/** Checks whether a value is an iterator (has a next function) */
export const isIterator = (value) => Boolean(value) && isFunction(value.next);
/** Checks whether a value is a fetch Response or a duck-typed equivalent */
export const isResponse = (value) => (typeof Response !== 'undefined' && value instanceof Response) ||
    (isObject(value) &&
        isFunction(value.arrayBuffer) &&
        isFunction(value.text) &&
        isFunction(value.json));
/** Checks whether a value is a File */
export const isFile = (value) => typeof File !== 'undefined' && value instanceof File;
/** Checks whether a value is a Blob */
export const isBlob = (value) => typeof Blob !== 'undefined' && value instanceof Blob;
/** Checks for Node.js Buffers without triggering bundlers to include the Buffer polyfill */
export const isBuffer = (value) => Boolean(value &&
    typeof value === 'object' &&
    value.isBuffer &&
    'buffer' in value);
/** Checks whether a value looks like a DOM WritableStream */
export const isWritableDOMStream = (value) => isObject(value) &&
    isFunction(value.abort) &&
    isFunction(value.getWriter);
/** Checks whether a value looks like a DOM ReadableStream */
export const isReadableDOMStream = (value) => (typeof ReadableStream !== 'undefined' && value instanceof ReadableStream) ||
    (isObject(value) &&
        isFunction(value.tee) &&
        isFunction(value.cancel) &&
        isFunction(value.getReader));
// Not implemented in Firefox: && isFunction(x.pipeTo)
/** Checks whether a value looks like a Node.js writable stream */
export const isWritableNodeStream = (value) => isObject(value) &&
    isFunction(value.end) &&
    isFunction(value.write) &&
    isBoolean(value.writable);
/** Checks whether a value looks like a Node.js readable stream */
export const isReadableNodeStream = (value) => isObject(value) &&
    isFunction(value.read) &&
    isFunction(value.pipe) &&
    isBoolean(value.readable);
/** Checks whether a value is any readable stream (DOM or Node.js) */
export const isReadableStream = (value) => isReadableDOMStream(value) || isReadableNodeStream(value);
/** Checks whether a value is any writable stream (DOM or Node.js) */
export const isWritableStream = (value) => isWritableDOMStream(value) || isWritableNodeStream(value);
//# sourceMappingURL=is-type.js.map